import { useState, useRef, useEffect, useCallback } from 'react';
import { ZONE_COLORS } from '../galaxy/collisionGraphBuilder';
import '../styles/player.css';

const ZONES = [
  { key: 'core_overlap', label: 'Core Overlap', desc: 'Artists you both know and love' },
  { key: 'your_artists', label: 'Your Artists', desc: 'Your exclusive favorites' },
  { key: 'friend_artists', label: "Friend's Artists", desc: 'Their exclusive favorites' },
  { key: 'shared_frontier', label: 'Shared Frontier', desc: 'New artists to explore together' },
  { key: 'your_exploration', label: 'Incoming Signals', desc: "Their artists that connect to yours" },
  { key: 'friend_exploration', label: 'Outgoing Signals', desc: 'Your artists that connect to theirs' },
];

function ZoneSelector({ selectedZones, onSelectionChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const popupRef = useRef(null);
  const buttonRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (
        popupRef.current && !popupRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('pointerdown', handleClick);
    return () => document.removeEventListener('pointerdown', handleClick);
  }, [isOpen]);

  const handleToggleZone = useCallback((key) => {
    const next = new Set(selectedZones);
    if (next.has(key)) {
      // Don't allow deselecting all — keep at least one
      if (next.size > 1) next.delete(key);
    } else {
      next.add(key);
    }
    onSelectionChange(next);
  }, [selectedZones, onSelectionChange]);

  const allSelected = selectedZones.size === ZONES.length;

  const handleSelectAll = useCallback(() => {
    onSelectionChange(new Set(ZONES.map((z) => z.key)));
  }, [onSelectionChange]);

  // Build pill label
  let label;
  if (allSelected) {
    label = 'All Zones';
  } else if (selectedZones.size === 1) {
    const z = ZONES.find((z) => z.key === [...selectedZones][0]);
    label = z ? z.label : '1 zone';
  } else {
    label = `${selectedZones.size} zones`;
  }

  return (
    <div className="zone-selector-wrapper">
      <button
        ref={buttonRef}
        className="player-mode-btn active"
        onClick={() => setIsOpen((v) => !v)}
        title={`Exploring: ${[...selectedZones].map((k) => ZONES.find((z) => z.key === k)?.label).filter(Boolean).join(', ')}`}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
        <span className="player-mode-label">{label}</span>
        <svg className="zone-selector-caret" viewBox="0 0 12 12" fill="currentColor" width="8" height="8">
          <path d="M2 4l4 4 4-4" />
        </svg>
      </button>

      {isOpen && (
        <div ref={popupRef} className="zone-selector-popup">
          <button
            className={`zone-selector-item ${allSelected ? 'selected' : ''}`}
            onClick={handleSelectAll}
          >
            <span className="zone-selector-check">{allSelected ? '✓' : ''}</span>
            <div className="zone-selector-text">
              <span className="zone-selector-label">All Zones</span>
            </div>
          </button>
          {ZONES.map((z) => {
            const color = ZONE_COLORS[z.key];
            return (
              <button
                key={z.key}
                className={`zone-selector-item ${selectedZones.has(z.key) ? 'selected' : ''}`}
                onClick={() => handleToggleZone(z.key)}
              >
                <span className="zone-selector-check">{selectedZones.has(z.key) ? '✓' : ''}</span>
                <span
                  className="zone-selector-dot"
                  style={{ background: `hsl(${color.h}, ${color.s}%, ${color.l}%)` }}
                />
                <div className="zone-selector-text">
                  <span className="zone-selector-label">{z.label}</span>
                  <span className="zone-selector-desc">{z.desc}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ZoneSelector;
